import React, { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Alert, Checkbox, Form, Input, Modal, Select } from 'antd';
import { listAssignableSalesTeams, transferSalesRecord } from '@/services/salesTeam.service';
import { useAuthStore } from '@/store/authStore';

type SalesTeamTransferModalProps = {
  open: boolean;
  onClose: () => void;
  entityType: string;
  entityId: string;
  entityLabel?: string;
  currentTeamName?: string | null;
  onSuccess?: () => void;
};

function canTransferTeam(user: { is_super_admin?: boolean; permissions?: string[] } | null): boolean {
  if (!user) return false;
  if (user.is_super_admin) return true;
  const p = user.permissions ?? [];
  return (
    p.includes('growth.team.transfer') ||
    p.includes('growth.team.view_all') ||
    p.includes('company.view')
  );
}

export const SalesTeamTransferModal: React.FC<SalesTeamTransferModalProps> = ({
  open,
  onClose,
  entityType,
  entityId,
  entityLabel,
  currentTeamName,
  onSuccess,
}) => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const qc = useQueryClient();
  const { user } = useAuthStore();
  const allowed = canTransferTeam(user);

  const { data: teams = [] } = useQuery({
    queryKey: ['sales-teams-assignable'],
    queryFn: listAssignableSalesTeams,
    enabled: open && allowed,
  });

  const teamOptions = useMemo(
    () => teams.map((t) => ({ value: t.id, label: t.team_name })),
    [teams]
  );

  const transferMut = useMutation({
    mutationFn: transferSalesRecord,
    onSuccess: () => {
      message.success('Record transferred');
      qc.invalidateQueries();
      form.resetFields();
      onClose();
      onSuccess?.();
    },
    onError: (e: Error) => message.error(e.message || 'Transfer failed'),
  });

  const handleOk = async () => {
    if (!allowed) {
      message.error('You do not have permission to transfer sales records');
      return;
    }
    const values = await form.validateFields();
    await transferMut.mutateAsync({
      entity_type: entityType,
      entity_id: entityId,
      to_team_id: values.to_team_id as string,
      reason: values.reason as string | undefined,
      remarks: values.remarks as string | undefined,
      transfer_downstream: !!values.transfer_downstream,
    });
  };

  if (open && !allowed) {
    return (
      <Modal title="Transfer not allowed" open={open} onCancel={onClose} footer={null}>
        <Alert
          type="warning"
          showIcon
          message="Missing permission"
          description="sales.team.transfer (or sales.team.view_all) is required to transfer records between teams."
        />
      </Modal>
    );
  }

  return (
    <Modal
      title={`Transfer ${entityLabel || entityType}`}
      open={open}
      onCancel={onClose}
      onOk={handleOk}
      confirmLoading={transferMut.isPending}
      destroyOnClose
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="Transfer approval (company setting transfer_requires_approval)"
        description="When enabled in company settings, an approval workflow may apply in a future release. Direct transfer is used today."
      />
      {currentTeamName ? (
        <p style={{ marginBottom: 12 }}>
          Current team: <strong>{currentTeamName}</strong>
        </p>
      ) : null}
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 12 }}
        message="Downstream documents"
        description="When enabled, draft or open quotations, estimations, checklists, and related records linked to this document may also move to the new team."
      />
      <Form form={form} layout="vertical" initialValues={{ transfer_downstream: true }}>
        <Form.Item name="to_team_id" label="Transfer to team" rules={[{ required: true }]}>
          <Select showSearch optionFilterProp="label" options={teamOptions} placeholder="Select team" />
        </Form.Item>
        <Form.Item name="reason" label="Reason">
          <Input.TextArea rows={2} maxLength={500} />
        </Form.Item>
        <Form.Item name="remarks" label="Remarks (audit)">
          <Input.TextArea rows={2} maxLength={500} />
        </Form.Item>
        <Form.Item name="transfer_downstream" valuePropName="checked">
          <Checkbox>Also transfer draft/open downstream documents</Checkbox>
        </Form.Item>
      </Form>
    </Modal>
  );
};
